import {
  Pagination,
  Radio,
  Button as AntdButton,
  RadioChangeEvent,
  TablePaginationConfig,
  theme,
  Input,
  Select,
  Space,
} from 'antd'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {PlantsTable, PlantsTableProps} from '../plantsTable/PlantsTable'
import {useIntl} from 'react-intl'
import {PlantsGallery} from '../plantsGallery/PlantsGallery'
import {PlantsCardListView} from '../plantsCardsListView/PlantsCardListView'
import {
  instance as plantsService,
  PlantsSortingField,
  plantsSortingFields,
} from '../../services/PlantsService'
import {PlantedPlantsFilters} from '../../services/PlantsService'
import {useNavigate, useRouteLoaderData, useSearchParams} from 'react-router-dom'
import {PlantsFilter} from './PlantsFilter'
import {PlantCollection, PlantWithProjectName} from '../../entities/plant'
import {
  AppstoreOutlined,
  PictureOutlined,
  TableOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import debounce from 'lodash/debounce'
import './plantsPage.less'

const INFINITE_SCROLL_PAGE_SIZE = plantsService.DEFAULT_PER_PAGE

interface TableParams {
  pagination?: TablePaginationConfig
  filters?: PlantedPlantsFilters
}

export function PlantsPage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // View mode lives in the URL rather than component state, so it shares a
  // single source of truth with the data params. Cards is the default and
  // carries no `view` param; Gallery and Table are explicit.
  const viewParam = searchParams.get('view')
  const viewMode = viewParam === 'Gallery' || viewParam === 'Table' ? viewParam : 'Cards'

  const {
    token: {colorBgContainer},
  } = theme.useToken()

  const {userPlants, userPlantCollections, totalCount, tableParams, dataKey} = useRouteLoaderData(
    'plantsList'
  ) as {
    userPlants: PlantWithProjectName[]
    userPlantCollections: PlantCollection[]
    totalCount: number
    tableParams: {
      page: number
      perPage: number
      sortField?: PlantsSortingField
      sortOrder?: string
      filters: PlantedPlantsFilters
    }
    dataKey: number
  }

  const viewModes = [
    {
      label: (
        <span>
          <AppstoreOutlined /> {intl.formatMessage({id: 'cards'})}
        </span>
      ),
      value: 'Cards',
    },
    {
      label: (
        <span>
          <PictureOutlined /> {intl.formatMessage({id: 'gallery'})}
        </span>
      ),
      value: 'Gallery',
    },
    {
      label: (
        <span>
          <TableOutlined /> {intl.formatMessage({id: 'table'})}
        </span>
      ),
      value: 'Table',
    },
  ]

  const viewPageSizes = useMemo(() => {
    const availH = window.innerHeight - 320
    const tableSize = Math.max(5, Math.floor((availH - 57) / 54))
    return {
      Table: tableSize,
      Gallery: INFINITE_SCROLL_PAGE_SIZE,
      Cards: INFINITE_SCROLL_PAGE_SIZE,
    } as Record<string, number>
  }, [])

  // Keep perPage in sync with the active view's page size. This covers mount and
  // directly-loaded URLs (e.g. a shared `?view=Table` link); a view switch sets
  // perPage atomically in onViewModeChange, so this effect is a no-op then. The
  // page is intentionally left untouched here - resetting it on a view change is
  // owned by onViewModeChange, so a bookmarked deep page survives a refresh.
  useEffect(() => {
    const targetSize = viewPageSizes[viewMode]
    if (!targetSize || tableParams.perPage === targetSize) return
    setSearchParams(
      (current) => {
        const params = Object.fromEntries(current)
        if (targetSize === plantsService.DEFAULT_PER_PAGE) delete params.perPage
        else params.perPage = String(targetSize)
        return params
      },
      {replace: true}
    )
  }, [viewMode, viewPageSizes, tableParams.perPage])

  const [name, setName] = useState(tableParams.filters.plantName || '')

  const updateFilters = useCallback(
    (updater: (current: PlantedPlantsFilters) => PlantedPlantsFilters) => {
      const filters = updater(tableParams.filters)
      setSearchParams((current) => {
        const params = Object.fromEntries(current)
        const cleanedUpFilters: Record<string, unknown> = {}
        for (const key of Object.keys(filters)) {
          const value = filters[key as keyof PlantedPlantsFilters]
          if (!value) continue
          if (Array.isArray(value) && value.length === 0) continue
          cleanedUpFilters[key] = value
        }
        const jsonFilters = JSON.stringify(cleanedUpFilters)
        if (jsonFilters === '{}') delete params.filter
        else params.filter = jsonFilters
        if (params.page) delete params.page
        return params
      })
    },
    [tableParams.filters, setSearchParams]
  )

  const setNameDebounced = useMemo(
    () =>
      debounce(
        (newName: string) => updateFilters((current) => ({...current, plantName: newName})),
        300
      ),
    [updateFilters]
  )

  useEffect(() => {
    setNameDebounced(name)
  }, [name])

  // Infinite scroll state for Cards and Gallery. The loader provides the first
  // page (userPlants); extraPlants accumulates the pages fetched afterwards, so
  // it starts from page 2.
  const [extraPlants, setExtraPlants] = useState<PlantWithProjectName[]>([])
  const [nextPage, setNextPage] = useState(2)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Reset the accumulated infinite-scroll pages whenever the loader returns a
  // new dataset (sort/filter change, or a revalidation after an add/edit).
  // Doing this during render - guarded by the stable dataKey the loader stamps
  // on every run - lets React discard this render pass before it commits, so
  // the new first page is never painted combined with the previous query's
  // stale extra pages (which would otherwise flash duplicate/stale rows).
  const [loadedDataKey, setLoadedDataKey] = useState(dataKey)
  if (loadedDataKey !== dataKey) {
    setLoadedDataKey(dataKey)
    setExtraPlants([])
    setNextPage(2)
  }

  const isInfiniteScrollMode = viewMode === 'Cards' || viewMode === 'Gallery'

  const allPlants = useMemo(
    () => (isInfiniteScrollMode ? [...userPlants, ...extraPlants] : userPlants),
    [isInfiniteScrollMode, userPlants, extraPlants]
  )

  const hasMore = allPlants.length < totalCount

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    try {
      const result = await plantsService.getUserFilteredPlants(
        {
          page: nextPage,
          perPage: INFINITE_SCROLL_PAGE_SIZE,
          filters: tableParams.filters,
          sort: tableParams.sortField
            ? {
                field: tableParams.sortField,
                direction: (tableParams.sortOrder || 'asc') as 'asc' | 'desc',
              }
            : null,
        },
        intl.locale as 'en' | 'ru'
      )
      // Only grow the list (and advance the page) when the page actually
      // returned rows. Appending an empty result would still produce a new array
      // reference, re-triggering the fill check and looping if the backend's
      // count and rows ever disagree.
      if (result.plants.length === 0) return
      setExtraPlants((prev) => [...prev, ...result.plants])
      setNextPage((prev) => prev + 1)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, hasMore, nextPage, tableParams, intl.locale])

  const handlePaginationChange = (current: number, pageSize: number) =>
    setSearchParams((searchParams) => {
      const page = current
      const perPage = pageSize

      const params = Object.fromEntries(searchParams)
      if (page !== 1) {
        params.page = String(page)
      } else {
        delete params.page
      }
      if (perPage !== plantsService.DEFAULT_PER_PAGE) {
        params.perPage = String(perPage)
      } else {
        delete params.perPage
      }
      return params
    })

  const onViewModeChange = ({target: {value}}: RadioChangeEvent) => {
    setSearchParams((current) => {
      const params = Object.fromEntries(current)
      // Cards is the default view and carries no param.
      if (value === 'Cards') delete params.view
      else params.view = value
      // The page size follows the view; omit it when it equals the default.
      const targetSize = viewPageSizes[value]
      if (targetSize && targetSize !== plantsService.DEFAULT_PER_PAGE) {
        params.perPage = String(targetSize)
      } else {
        delete params.perPage
      }
      // Switching the view returns to the first page.
      delete params.page
      return params
    })
  }

  const pagination: TablePaginationConfig = {
    current: tableParams.page,
    pageSize: tableParams.perPage,
    total: totalCount,
  }

  const viewProps: PlantsTableProps = {
    getPlantLink: (plant) => `/plants/${plant.id}`,
    parentRoute: '/plants',
    plants: isInfiniteScrollMode ? allPlants : userPlants,
    isLoading: false,
    newPlantRoute: `/plants/new`,
    ...(isInfiniteScrollMode && {loadMore, hasMore, isLoadingMore}),
  }

  return (
    <div className="plants-page" style={{background: colorBgContainer}}>
      <div className="plants-page__header">
        <div className="plants-page__header__title">
          <svg
            className="plants-page__header__title__icon"
            width="29.096069"
            height="50.079987"
            viewBox="0 0 58.192139 100.15997"
            fill="none"
            role="img"
            aria-labelledby="title desc"
            version="1.1"
            id="svg7"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs id="defs7" />
            <g
              opacity="0.95"
              stroke="#6f8f72"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              id="g7"
              transform="translate(-91.361043,-20.340025)"
            >
              <path d="m 96,118 h 48" opacity="0.35" id="path1" />
              <path d="m 101,91 h 38 l -5.5,29 h -27 z" fill="#e7efe3" id="path2" />
              <path d="M 120,91 V 48" id="path4" />
              <path
                d="m 120,69 c -10.5,-8 -19,-9.5 -28,-6 3.5,9.5 13.5,14.5 28,6 z"
                fill="#ddebdc"
                id="path5"
              />
              <path
                d="m 121,58 c 9.5,-10 18,-13 28,-10 -1.5,11 -12.5,18.5 -28,10 z"
                fill="#ddebdc"
                id="path6"
              />
              <path
                d="m 119,47 c -7,-8 -8.5,-17 -4.5,-26 9,4 12.5,14.5 4.5,26 z"
                fill="#ddebdc"
                id="path7"
              />
            </g>
          </svg>
          <div>
            <h2 className="todos">{intl.formatMessage({id: 'myPlants'})}</h2>
            <div className="plants-page__header__subtitle">{`${totalCount} ${intl.formatMessage({
              id: 'plants',
            })}`}</div>
          </div>
        </div>

        <Input
          className="plants-page__header__search"
          size="large"
          placeholder={intl.formatMessage({id: 'plantName'})}
          prefix={<SearchOutlined />}
          allowClear
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="plants-page__header__settings">
          <Radio.Group
            className="view-modes"
            size="large"
            options={viewModes}
            onChange={onViewModeChange}
            value={viewMode}
            optionType="button"
            buttonStyle="solid"
          />
        </div>

        <AntdButton
          size="large"
          className="plants-page__header__add-plant-button"
          type="primary"
          onClick={() => {
            navigate(`/plants/new` + location.search)
          }}
        >
          {intl.formatMessage({id: 'addPlant'})}...
        </AntdButton>
      </div>
      <div className="plants-page__toolbar">
        <PlantsFilter
          filters={tableParams.filters}
          setFilters={updateFilters}
          userPlantCollections={userPlantCollections}
        />
        <Select
          value={tableParams.sortField}
          style={{minWidth: 180}}
          prefix={intl.formatMessage({id: 'sortBy'}) + ':'}
          onChange={(sortField) =>
            setSearchParams((current) => {
              const params = Object.fromEntries(current)
              params.sortField = sortField
              // A different ordering changes which rows fall on the first page,
              // so jump back to it (relevant for the paginated Table view).
              delete params.page
              return params
            })
          }
          options={plantsSortingFields.map((field) => ({
            value: field,
            label: intl.formatMessage({id: field}),
          }))}
        />
        <AntdButton
          icon={
            tableParams.sortOrder === 'desc' ? (
              <SortDescendingOutlined />
            ) : (
              <SortAscendingOutlined />
            )
          }
          onClick={() =>
            setSearchParams((current) => {
              const params = Object.fromEntries(current)
              params.sortOrder = tableParams.sortOrder === 'desc' ? 'asc' : 'desc'
              delete params.page
              return params
            })
          }
        />
      </div>

      {viewMode === 'Table' && (
        <div>
          <PlantsTable {...viewProps} />
        </div>
      )}

      {viewMode === 'Gallery' && (
        <div>
          <PlantsGallery {...viewProps} />
        </div>
      )}

      {viewMode === 'Cards' && (
        <div>
          <PlantsCardListView {...viewProps} />
        </div>
      )}

      {viewMode === 'Table' && (
        <Pagination
          style={{justifyContent: 'flex-end', paddingTop: '20px'}}
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={handlePaginationChange}
        />
      )}
    </div>
  )
}
